import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Truck, Heart, ArrowRight, Check } from "lucide-react";

const SEEN_KEY = "pawlinx_shipping_promo_seen";

const PETS = [
  { src: "https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&w=400&q=80", alt: "Happy dog" },
  { src: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&w=400&q=80", alt: "Cat" },
  { src: "https://images.unsplash.com/photo-1552728089-57bdde30beb3?auto=format&fit=crop&w=400&q=80", alt: "Bird" },
];


export default function ShippingPromoPopup() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/") return;
    let seen = false;
    try { seen = sessionStorage.getItem(SEEN_KEY) === "1"; } catch { /* ignore */ }
    if (seen) return;
    const tmr = setTimeout(() => setOpen(true), 2600);
    return () => clearTimeout(tmr);
  }, [location.pathname]);

  const close = () => {
    setOpen(false);
    try { sessionStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
  };

  const getEstimate = () => {
    close();
    window.dispatchEvent(new Event("open-estimate-modal"));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm sm:px-4" data-testid="shipping-promo">
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl">
        <button onClick={close} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-paw-navy flex items-center justify-center shadow" data-testid="shipping-promo-close">
          <X className="w-4 h-4" />
        </button>

        {/* Pet trio */}
        <div className="grid grid-cols-3 gap-0.5 h-36 sm:h-40">
          {PETS.map((p) => (
            <div key={p.alt} className="bg-cover bg-center" style={{ backgroundImage: `url(${p.src})` }} role="img" aria-label={p.alt} />
          ))}
        </div>

        <div className="p-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-paw-primary/10 text-paw-primary px-3 py-1 text-[12px] font-semibold font-manrope mb-3">
            <Truck className="w-3.5 h-3.5" /> Ongoing client reward
          </div>
          <h2 className="font-outfit font-extrabold text-[22px] leading-tight text-paw-navy">
            Always <span className="text-paw-primary">50% off delivery</span> on every refill.
          </h2>
          <p className="font-manrope text-sm text-paw-text-secondary mt-2">
            Once you're a PawLinx client, the shipping cost on every refill is half price, for as long as your pet stays on their medication.
          </p>

          <div className="mt-4 rounded-xl bg-paw-cream border border-paw-border p-4">
            <div className="flex items-center justify-between">
              <p className="font-manrope text-[13px] text-paw-navy font-medium">Refill delivery</p>
              <p className="font-outfit font-extrabold text-[18px] text-paw-success leading-none">50% OFF</p>
            </div>
            <p className="font-manrope text-[12px] text-paw-text-secondary mt-2">
              Applies to <strong>refill orders</strong> placed while your original prescription is still valid, not to brand-new orders.
            </p>
          </div>
          <p className="font-manrope text-[11px] text-paw-text-muted mt-2 flex items-center gap-1">
            <Check className="w-3 h-3 text-paw-success" /> Place a refill before your prescription expires to keep the discount.
          </p>

          <div className="mt-4 flex items-start gap-2 rounded-xl bg-paw-primary/5 border border-paw-primary/15 px-3.5 py-3">
            <Heart className="w-4 h-4 text-paw-primary fill-current shrink-0 mt-0.5" />
            <p className="font-manrope text-[12.5px] text-paw-navy leading-relaxed">
              Order through us and with every delivery we donate on your behalf to help fund life-saving pet surgeries.
            </p>
          </div>

          <button onClick={getEstimate} className="mt-5 w-full bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full h-12 font-manrope font-semibold text-[15px] flex items-center justify-center gap-2 transition-colors" data-testid="shipping-promo-cta">
            Get an instant estimate <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={close} className="mt-2 w-full text-center font-manrope text-[12px] text-paw-text-muted hover:text-paw-text py-1">
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
